import { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Typography,
  IconButton,
  Skeleton,
  Chip,
} from "@mui/material";
import { styled } from "@mui/system";
import { IoArrowBack } from "react-icons/io5";
import { FiEdit, FiSave } from "react-icons/fi";
import { FaFileAlt, FaRegNewspaper } from "react-icons/fa";
import DeleteIcon from '@mui/icons-material/Delete';
// import { styled } from '@mui/material/styles';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import ComputerIcon from '@mui/icons-material/Computer';

const HeaderContainer = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  left: 0,
  width: "100%",
  backgroundColor: "#ffffff",
  zIndex: 1000,
  "@media (max-width: 600px)": {
    padding: "12px 16px",
  },
}));

const ButtonContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

const StyledButton = styled(Button)({
  width: "8em",
  borderRadius: "5px",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  "&:focus": {
    outline: "2px solid #1976d2",
    outlineOffset: "2px",
  },
});

const HeaderLayout2 = ({
  actionBtn,
  newData,
  handleBack,
  handleSave,
  handleEdit,
  handleGeneratePayslip,
  generatePayslip,
  isLoading,
  postBtn = false,
  handlePost,
  status,
  returnToDraft=false,
  computePayslip =false,
  handleComputePayslip,
  handleResetToDraft,
  editBtn=true,
  headerRightExtra,
  headerLeftExtra,
  deleteBtn = false,
  handleDelete
}) => {
  return (
    <HeaderContainer>
      <Box display={"flex"} justifyContent={"space-between"}>
        <ButtonContainer>
          {/* Back Button (Always Visible) */}
          <IconButton
            onClick={handleBack}
            aria-label="go back"
            sx={{
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            <IoArrowBack size={24} />
          </IconButton>

          {/* Action Buttons */}
          {isLoading ? (
            <>
              {/* Skeleton for Save/Edit Button */}
              <Skeleton variant="rectangular" width={120} height={40} />
              {generatePayslip && (
                <Skeleton variant="rectangular" width={180} height={40} />
              )}
            </>
          ) : (
            <>
              {actionBtn && (
                <>
                  {newData ? (
                    <StyledButton
                      variant="outlined"
                      color="primary"
                      onClick={handleSave}
                      startIcon={<FiSave />}
                      aria-label="save changes"
                    >
                      Save
                    </StyledButton>
                  ) : (
                    <>
                      {editBtn && <StyledButton
                        variant="outlined"
                        color="primary"
                        onClick={handleEdit}
                        startIcon={<FiEdit />}
                        aria-label="edit content"
                      >
                        Edit
                      </StyledButton>}
                      {postBtn && (
                        <StyledButton
                          variant="outlined"
                          color="primary"
                          onClick={handlePost}
                          startIcon={<FaRegNewspaper />}
                          aria-label="post payslip"
                        >
                          Post
                        </StyledButton>
                      )}
                      {deleteBtn && (
                        <StyledButton
                          variant="outlined"
                          color="error"
                          onClick={handleDelete}
                          startIcon={<DeleteIcon />}
                          aria-label="delete draft"
                        >
                          Delete
                        </StyledButton>
                      )}
                    </>
                  )}
                </>
              )}
              {computePayslip && (
                <StyledButton
                  style={{ width: "15em" }}
                  variant="outlined"
                  color="primary"
                  onClick={handleComputePayslip}
                  startIcon={<ComputerIcon />}
                  aria-label="compute"
                >
                  Compute
                </StyledButton>
              )}
                {returnToDraft && (
                <StyledButton
                  style={{ width: "15em" }}
                  variant="outlined"
                  color="primary"
                  onClick={handleResetToDraft}
                  startIcon={<KeyboardReturnIcon />}
                  aria-label="return to draft"
                >
                  Reset to draft
                </StyledButton>
              )}
                 {generatePayslip && (
                <StyledButton
                  style={{ width: "15em" }}
                  variant="outlined"
                  color="primary"
                  onClick={handleGeneratePayslip}
                  startIcon={<FaFileAlt />}
                  aria-label="generate payslip"
                >
                  Generate payslip
                </StyledButton>
              )}
            </>
          )}
          {headerLeftExtra && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {headerLeftExtra}
            </Box>
          )}
        </ButtonContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {headerRightExtra}
          <StatusDisplay status={status?.toLowerCase() }/>
        </Box>
      </Box>
      <Divider sx={{ mt: 2 }} />
    </HeaderContainer>
  );
};

export default HeaderLayout2;

// Styled Chip component with custom status-based colors
const StatusChip = styled(Chip)(({ status }) => ({
  textTransform: "capitalize",
  ...(status === "posted" && {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
  }),
  ...((status === "drafted" || status === "draft") && {
    backgroundColor: "#fff3e0",
    color: "#f57c00",
  }),
  ...(status === "pending" && {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
  }),
  ...(status === "archived" && {
    backgroundColor: "#f5f5f5",
    color: "#616161",
  }),
  ...(status === "new" && {
    backgroundColor: "#fce4ec",
    color: "#c2185b",
  }),
  ...(status === "done" && {
    backgroundColor: "#e0f2f1",
    color: "#00796b",
  }),
  ...(status === "computed" && {
    backgroundColor: "#ede7f6", 
    color: "#5e35b1",        
  }),
}));

// Status Display Component
const StatusDisplay = ({ status }) => {
  // Default to 'unknown' if status is not provided or invalid
  const validStatuses = ["posted", "drafted", "pending", "archived", "new", "draft", "done", "computed"];
  const normalizedStatus = status?.toLowerCase();
  const isValidStatus = validStatuses.includes(normalizedStatus);
  const displayStatus = isValidStatus ? normalizedStatus : "unknown";
  const displayLabel = normalizedStatus === "computed" ? "in progress" : displayStatus;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <StatusChip
        label={displayLabel}
        status={displayStatus}
        size="small"
      />
    </Box>
  );
};
